import React, { useEffect, useState } from 'react';
import { cn } from 'cn';
import {
  Popover as UIPopover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';

// O menu dos lookups (select2, select-async) abre em portal, fora do popover:
// sem isso, escolher uma opção conta como clique fora e fecha o popover.
function handleInteractOutside(event: Event) {
  const target = event.target as Element | null;
  if (target?.closest?.('.select-menu-portal')) event.preventDefault();
}

type PopoverProps = {
  /** O que aparece dentro do popover. */
  content?: React.ReactNode;
  /** Cabeçalho em negrito, acima do conteúdo. */
  title?: React.ReactNode;
  /** Lado do gatilho em que o popover abre. */
  placement?: 'top' | 'right' | 'bottom' | 'left';
  /** Alinhamento em relação ao gatilho, no eixo do lado escolhido. */
  align?: 'start' | 'center' | 'end';
  /** Onde o popover é renderizado: o elemento ou o id dele. Use na tela cheia (ex.: Card de Metas), senão o popover abre por trás. */
  container?: HTMLElement | string;
  /** Classes do conteúdo (ex.: `tw:p-0` para tirar o padding). */
  className?: string;
  /** Aberto ou fechado, quando a tela controla o estado. Sem ele, o popover se controla sozinho. */
  open?: boolean;
  /** Chamado ao abrir e ao fechar, com o novo estado. */
  onOpenChange?: (open: boolean) => void;
  /** Força fechado e não abre no clique. */
  disabled?: boolean;
  /** O gatilho: um único elemento que aceite ref (ex.: o `Button` do portal). */
  children: React.ReactElement;
};

/**
 * Conteúdo flutuante que abre ao clicar no gatilho e fecha ao clicar fora. Para abrir ao passar o mouse, use o `HoverCard`.
 * @import import Popover from 'src/componentes/popover';
 */
const Popover = ({
  content,
  title,
  placement = 'bottom',
  align,
  container,
  className,
  open,
  onOpenChange,
  disabled,
  children,
}: PopoverProps) => {
  const [aberto, setAberto] = useState(false);
  const isOpen = !disabled && (open ?? aberto);

  useEffect(() => {
    if (!disabled) return;
    setAberto(false);
    if (open) onOpenChange?.(false);
  }, [disabled]);

  const handleOpenChange = (value: boolean) => {
    if (disabled) return;
    setAberto(value);
    onOpenChange?.(value);
  };

  // container pode ser o id de um elemento que ainda não existe quando a tela
  // monta (ex.: o card de metas em tela cheia); por isso é buscado a cada
  // render. Não depende de isOpen: trocar o alvo do portal durante a animação
  // de saída remontaria o conteúdo no body.
  const portalContainer =
    typeof container === 'string'
      ? document.getElementById(container) || undefined
      : container;

  return (
    <UIPopover open={isOpen} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>{children}</PopoverTrigger>
      <PopoverContent
        side={placement}
        align={align}
        container={portalContainer}
        className={cn('tw:w-auto', className)}
        onInteractOutside={handleInteractOutside}
      >
        {title && <div className="tw:mb-2 tw:font-semibold">{title}</div>}
        {content}
      </PopoverContent>
    </UIPopover>
  );
};

export default Popover;
